import React from "react";
import { styled, keyframes, css } from "@mui/material/styles";
import { useOrdersStore } from "../../stores/orders/orders-store";
import { updateOrderStatus } from "../../stores/orders/orders-actions";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import DoneAllIcon from "@mui/icons-material/DoneAll";

const getStatusColor = (
  statusName: string
): { base: string; shadow: string; hover: string } => {
  const name = statusName.toLowerCase();
  if (name.includes("new"))
    return {
      base: "#3B82F6",
      shadow: "rgba(59, 130, 246, 0.25)",
      hover: "rgba(59, 130, 246, 0.4)"
    };
  if (name.includes("accepted"))
    return {
      base: "#F59E0B",
      shadow: "rgba(245, 158, 11, 0.25)",
      hover: "rgba(245, 158, 11, 0.4)"
    };
  if (name.includes("progress"))
    return {
      base: "#8B5CF6",
      shadow: "rgba(139, 92, 246, 0.25)",
      hover: "rgba(139, 92, 246, 0.4)"
    };
  if (name.includes("complete"))
    return {
      base: "#10B981",
      shadow: "rgba(16, 185, 129, 0.25)",
      hover: "rgba(16, 185, 129, 0.4)"
    };
  return {
    base: "#6B7280",
    shadow: "rgba(107, 114, 128, 0.25)",
    hover: "rgba(107, 114, 128, 0.4)"
  };
};

const slideUpFadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const StatusSection = styled("div")<{ isVisible: boolean; minHeight: string }>(
  ({ isVisible, minHeight }) => ({
    width: "100%",
    minHeight: minHeight,
    display: isVisible ? "flex" : "none",
    flexDirection: "column",
    overflow: "hidden",
    background: "#f0f2f5"
  })
);

const StatusHeader = styled("div")({
  padding: "12px 20px",
  fontSize: "16px",
  fontWeight: 600,
  color: "#4a5568",
  background: "#fff",
  borderBottom: "1px solid #e2e8f0",
  textAlign: "left",
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center"
});

const OrderCount = styled("span")({
  background: "#e2e8f0",
  color: "#4a5568",
  padding: "2px 8px",
  borderRadius: "12px",
  fontSize: "14px",
  fontWeight: 700
});

const OrdersList = styled("div")({
  flex: 1,
  overflowY: "auto",
  padding: "8px 16px",
  "&::-webkit-scrollbar": {
    display: "none"
  },
  "-ms-overflow-style": "none",
  "scrollbar-width": "none"
});

const OrderItem = styled("div")<{ $index: number; statusColor: string }>`
  background: #ffffff;
  display: flex;
  align-items: stretch;
  color: #2d3748;
  border-radius: 8px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.07),
    0 2px 4px -1px rgba(0, 0, 0, 0.04);
  margin: 12px 0;
  padding-left: 0;
  border-left: 6px solid ${({ statusColor }) => statusColor};
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.4s ease;
  animation: ${slideUpFadeIn} 0.5s ease-out forwards;
  animation-delay: ${({ $index }) => `${$index * 0.07}s`};
  opacity: 0;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.07),
      0 4px 6px -2px rgba(0, 0, 0, 0.05);
  }
`;

const InfoContainer = styled("div")({
  flex: 1,
  display: "flex",
  alignItems: "center",
  padding: "16px",
  lineHeight: "1.6"
});

const CustomerColumn = styled("div")({
  width: "200px",
  flexShrink: 0,
  fontWeight: 600,
  fontSize: "18px",
  color: "#2d3748",
  borderRight: "1px solid #e2e8f0",
  paddingRight: "16px"
});

const DetailsColumn = styled("div")({
  flex: 1,
  paddingLeft: "16px",
  fontSize: "15px",
  color: "#4a5568",
  lineHeight: "1.6"
});

const ActionButton = styled("button")<{
  statusColor: string;
  shadowColor: string;
  hoverShadowColor: string;
}>`
  background: ${({ statusColor }) => statusColor};
  width: 160px;
  flex-shrink: 0;
  box-shadow: 0 4px 14px 0 ${({ shadowColor }) => shadowColor};
  border: none;
  margin: 0;
  text-align: center;
  color: #ffffff;
  font-weight: bold;
  cursor: pointer;
  font-size: 16px;
  border-radius: 0 8px 8px 0;
  outline: none;
  transition: all 0.25s ease-in-out;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  & .icon-spin {
    animation: ${spin} 1.5s linear infinite;
  }

  &:hover:not(:disabled) {
    box-shadow: 0 6px 20px 0 ${({ hoverShadowColor }) => hoverShadowColor};
    transform: translateY(-3px);
  }

  &:active:not(:disabled) {
    transform: translateY(-1px) scale(0.98);
    box-shadow: 0 2px 8px 0 ${({ hoverShadowColor }) => hoverShadowColor};
  }

  &:disabled {
    background: #bdbdbd;
    box-shadow: none;
    cursor: not-allowed;
  }
`;

interface OrderStatusSectionProps {
  statusId: string;
  statusName: string;
  isVisible: boolean;
}

const OrderActionButton: React.FC<{
  order: any;
  onClick: () => void;
  statusColor: { base: string; shadow: string; hover: string };
}> = ({ order, onClick, statusColor }) => {
  const { isProcessing, buttonText } = order;

  let icon = <CheckCircleOutlineIcon />;
  if (buttonText.includes("Start") || buttonText.includes("Accept")) {
    icon = <PlayCircleOutlineIcon />;
  } else if (buttonText.includes("Complete") || buttonText.includes("Done")) {
    icon = <DoneAllIcon />;
  } else if (buttonText.includes("Hide")) {
    icon = <CheckCircleOutlineIcon />;
  }

  return (
    <ActionButton
      disabled={isProcessing}
      onClick={onClick}
      statusColor={statusColor.base}
      shadowColor={statusColor.shadow}
      hoverShadowColor={statusColor.hover}
    >
      {isProcessing ? <AutorenewIcon className="icon-spin" /> : icon}
      <span>{isProcessing ? "Processing" : buttonText}</span>
    </ActionButton>
  );
};

const OrderStatusSection: React.FC<OrderStatusSectionProps> = ({
  statusId,
  statusName,
  isVisible
}) => {
  const { orders, visibleViews } = useOrdersStore();
  const statusOrders = orders.get(statusId) || [];
  const statusColor = getStatusColor(statusName);

  const handleStatusUpdate = (orderId: string) => {
    updateOrderStatus(orderId, statusId);
  };

  const getMinHeight = () => {
    const visibleCount = visibleViews.length;
    if (visibleCount === 1) return "100%";
    if (visibleCount === 2) return "50%";
    if (visibleCount === 3) return "33%";
    return "25%";
  };

  return (
    <StatusSection isVisible={isVisible} minHeight={getMinHeight()}>
      <StatusHeader>
        {statusName}
        <OrderCount>{statusOrders.length}</OrderCount>
      </StatusHeader>
      <OrdersList>
        {statusOrders.map((order: any, index: number) => (
          <OrderItem
            key={order.id}
            $index={index}
            statusColor={statusColor.base}
          >
            <InfoContainer>
              <CustomerColumn>{order.customerName}</CustomerColumn>
              <DetailsColumn
                dangerouslySetInnerHTML={{ __html: order.orderDetails }}
              />
            </InfoContainer>
            <OrderActionButton
              order={order}
              onClick={() => handleStatusUpdate(order.id)}
              statusColor={statusColor}
            />
          </OrderItem>
        ))}
      </OrdersList>
    </StatusSection>
  );
};

export default OrderStatusSection;
